import React, { useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useNavigate } from "react-router-dom";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import TablePagination from "@mui/material/TablePagination";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { ThemeProvider, createTheme } from "@mui/material/styles";
import { ptBR } from "@mui/material/locale";

import { getComparator } from "../../utils/functionTable";
import EnhancedTableHead from "../../components/enhancedTableHead";
import { toast } from "react-toastify";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";

const headCells = [
  { id: "nickname", label: "Apelido", sortable: true },
  { id: "name", label: "Nome", sortable: true },
  { id: "email", label: "E-mail", sortable: false },
  { id: "telephone", label: "Telefone", sortable: false },
  { id: "address", label: "Endereço", sortable: false },
  { id: "city", label: "Cidade", sortable: false },
  { id: "birthday", label: "Aniversário", sortable: false },
];

export default function UsersTable({ id, data, search, isAdmin, onMemberDeleted }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("nickname");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();
  const { token } = useAuth();

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleRequestSort = (_, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/api/member-delete/${deleteTarget._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Membro "${deleteTarget.nickname}" excluído com sucesso.`);
      setDeleteTarget(null);
      onMemberDeleted();
    } catch {
      toast.error("Erro ao excluir membro. Tente novamente.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const theme = createTheme(ptBR);
  const filterSearch = (i, term) =>
    String(i).toLowerCase().includes(term.toLowerCase());

  const adminHeadCells = isAdmin
    ? [...headCells, { id: "actions", label: "Ações", sortable: false }]
    : headCells;

    // Função para converter AAAA-MM-DD em DD/MM/AAAA
  const formatDateBR = (dateString) => {
    if (!dateString) return "-";
    const [year, month, day] = dateString.split("-");
    if (!year || !month || !day) return dateString;
    return `${day}/${month}/${year}`;
  };

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return "";
    const cleaned = ("" + phoneNumber).replace(/\D/g, "");

    // Caso para DDI +1
    if (cleaned.startsWith("1") && cleaned.length === 11) {
      const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`;
      }
    }

    // Caso geral para outros DDIs
    const match = cleaned.match(/^(\d{2})(\d{2})(\d{4,5})(\d{4})$/);
    if (match) {
      return `+${match[1]} (${match[2]}) ${match[3]}-${match[4]}`;
    }

    return phoneNumber;
  };


  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

  return (
    <ThemeProvider theme={theme}>
      <TableContainer component={Paper}>
        <Table>
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={data.length}
            headCells={adminHeadCells}
          />
          <TableBody>
            {(rowsPerPage > 0
              ? data
                  .sort(getComparator(order, orderBy))
                  .filter(
                    (i) =>
                      filterSearch(i.name, search) ||
                      filterSearch(i.nickname, search) ||
                      filterSearch(i.email, search) ||
                      filterSearch(i.telephone, search) ||
                      filterSearch(i.city, search)
                  )
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              : data
                  .sort(getComparator(order, orderBy))
                  .filter(
                    (i) =>
                      filterSearch(i.name, search) ||
                      filterSearch(i.nickname, search) ||
                      filterSearch(i.email, search) ||
                      filterSearch(i.telephone, search) ||
                      filterSearch(i.city, search)
                  )
            ).map((row) => (
              <TableRow hover key={row._id}>
                <TableCell>{row.nickname || "-"}</TableCell>
                <TableCell>{row.name}</TableCell>
                <TableCell>{row.email || "-"}</TableCell>
                <TableCell style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}>{formatPhoneNumber(row.telephone) || "-"}</TableCell>
                <TableCell>{row.address || "-"}</TableCell>
                <TableCell>{row.city || "-"}</TableCell>
                <TableCell>{formatDateBR(row.birthday)}</TableCell>
                {isAdmin && (
                  <TableCell>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <IconButton
                        size="small"
                        color="primary"
                        title="Editar membro"
                        onClick={() => navigate(`/editProfile?userId=${row._id}`)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        title="Excluir membro"
                        onClick={() => setDeleteTarget(row)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!isMobile && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, { value: -1, label: "Todos" }]}
          component="div"
          count={data.length}
          labelRowsPerPage="Exibindo"
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            "& .MuiTablePagination-toolbar": {
              alignItems: "center",
              minHeight: "52px",
              justifyContent: "space-between",
              paddingLeft: 2,
              paddingRight: 2,
            },
            "& .MuiTablePagination-spacer": {
              flex: "1 1 100%",
            },
            "& .MuiTablePagination-selectLabel": {
              margin: 0,
              display: "flex",
              alignItems: "center",
              lineHeight: 1,
            },
            "& .MuiTablePagination-displayedRows": {
              margin: 0,
              display: "flex",
              alignItems: "center",
              lineHeight: 1,
            },
            "& .MuiTablePagination-select": {
              display: "flex",
              alignItems: "center",
              marginLeft: 1,
            },
            "& .MuiTablePagination-actions": {
              display: "flex",
              alignItems: "center",
              marginLeft: 2,
            },
            "& .MuiInputBase-root": {
              alignItems: "center",
            },
          }}
        />
      )}
      {isMobile && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, { value: -1, label: "Todos" }]}
          component="div"
          count={data.length}
          labelRowsPerPage="Exibindo"
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            display: "flex",
            alignItems: "center",
            "& .MuiTablePagination-toolbar": {
              alignItems: "center",
              minHeight: "52px",
              paddingLeft: 1,
              paddingRight: 1,
              display: "flex",
              width: "100%",
              justifyContent: "space-between",
            },
            "& .MuiTablePagination-spacer": {
              display: "none",
              flex: "none",
            },
            "& .MuiTablePagination-selectLabel": {
              margin: 0,
              display: "flex",
              alignItems: "left",
              lineHeight: 1,
              marginRight: 0.5,
            },
            "& .MuiTablePagination-displayedRows": {
              margin: 0,
              display: "none !important",
              alignItems: "center",
              lineHeight: 1,
            },
            "& .MuiTablePagination-select": {
              display: "flex",
              alignItems: "center",
              marginLeft: 0,
              marginRight: 0,
            },
            "& .MuiTablePagination-actions": {
              display: "flex",
              alignItems: "center",
              marginLeft: "auto",
              marginRight: 0,
            },
            "& .MuiInputBase-root": {
              alignItems: "space-between",
            },
          }}
        />
      )}

      {deleteTarget && (
        <div
          className="editprofile-delete-modal"
          onClick={() => setDeleteTarget(null)}
        >
          <div className="modal-backdrop" />
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirmar exclusão</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setDeleteTarget(null)}
                  aria-label="Fechar"
                />
              </div>
              <div className="modal-body" style={{ textAlign: "left" }}>
                Tem certeza que deseja excluir o membro{" "}
                <strong>{deleteTarget.nickname}</strong>?{" "}
                Esta ação não pode ser desfeita.
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleteLoading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteConfirm}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? "Excluindo..." : "Excluir"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </ThemeProvider>
  );
}
